import React, { useState, useEffect, useCallback, useRef } from 'react';
import CommonInput from '../common/CommonInput';
import CommonInputPhoneNumber from '../common/CommonInputPhoneNumber';

function PopupBtmModifyPhoneNumber({ setisShowPhoneNumber, setisCompletePhoneNumber, carInfo, setcarInfo }) {
  const [name, setName] = useState(carInfo.userName || '');
  const [phoneNumber, setPhoneNumber] = useState(carInfo.userPhoneNumber || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [isRequested, setIsRequested] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isError, setIsError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  const [phoneError, setPhoneError] = useState(false);
  const [isValidPhoneFormat, setIsValidPhoneFormat] = useState(true); // 초기값을 true로 변경
  const [isPhoneInputTouched, setIsPhoneInputTouched] = useState(false); // 새로운 상태 추가

  // 전화번호 유효성 검사 함수
  const validatePhoneNumber = (number) => {
    const phoneRegex = /^\d{3}-\d{4}-\d{4}$/;
    return phoneRegex.test(number);
  };

  // 전화번호 형식 체크
  useEffect(() => {
    if (!isPhoneInputTouched) return; // 입력이 시작되지 않았으면 검사하지 않음
    
    if (validatePhoneNumber(phoneNumber)) {
      setPhoneError(false);
      setIsValidPhoneFormat(true);
    } else {
      setPhoneError(true);
      setIsValidPhoneFormat(false);
    }
  }, [phoneNumber, isPhoneInputTouched]);

  // 전화번호 입력 처리 함수
  const handlePhoneNumberChange = (value) => {
    if (!isPhoneInputTouched) {
      setIsPhoneInputTouched(true); // 첫 입력 시 상태 변경
    }
    
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, '');
    
    // 11자리를 넘어가면 처리하지 않음
    if (numbers.length > 11) return;
    
    // 숫자를 구간별로 나누고 하이픈 추가
    let formattedNumber = '';
    if (numbers.length <= 3) {
      formattedNumber = numbers;
    } else if (numbers.length <= 7) {
      formattedNumber = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      formattedNumber = `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }

    setPhoneNumber(formattedNumber);
  };

  // Format remaining time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Timer effect
  useEffect(() => {
    let timer;
    if (isRequested && !isVerified && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRequested, isVerified, timeLeft]);

  // 인증요청 함수 수정
  const handleRequestVerification = () => {
    if (name.trim() && phoneNumber.trim()) {
      if (isValidPhoneFormat) {
        setIsRequested(true);
        setTimeLeft(180);
        setIsError(false);
        setPhoneError(false);
        setVerificationCode('');
      } else {
        setPhoneError(true);
      }
    }
  };

  // Verify the entered code
  const handleVerifyCode = () => {
    // Mock verification - in real app, this would call an API
    if (verificationCode === '123456') {
      setIsVerified(true);
      setIsError(false);
    } else {
      setIsError(true);
    }
  };

  // Complete the verification process
  const handleComplete = () => {
    if (isVerified) {
      setcarInfo({
        ...carInfo,
        userName: name,
        userPhoneNumber: phoneNumber
      });
      setisCompletePhoneNumber(true);
      setisShowPhoneNumber(false);
    }
  };

  // 스와이프 관련 상태
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const dragHandleRef = useRef(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    if (dragHandleRef.current?.contains(e.target)) {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientY);
    }
  };

  const onTouchMove = (e) => {
    if (touchStart) {
      setTouchEnd(e.targetTouches[0].clientY);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchEnd - touchStart;
    const isDownSwipe = distance > minSwipeDistance;
    
    if (isDownSwipe) {
      setisShowPhoneNumber(false);
    }
  };

  return (
    <div className="fixed bg-[rgba(0,0,0,0.7)] top-0 left-0 w-full h-full flex items-end justify-center z-10">
      <div 
        className="bg-white w-full pb-5 pt-10 min-h-64 rounded-t-2xl max-w-screen-md max-h-[calc(100%-67px)] overflow-y-auto relative px-5"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button 
          ref={dragHandleRef}
          className="absolute top-0 left-0 flex justify-center w-full h-10 pt-4 m-auto rounded-xl"
        >
          <span className="w-10 h-1 bg-[#E1E2E4] rounded-xl"></span>
        </button>
        
        <h3 className="text-[#1B1C1E] text-left font-semibold text-[22px] mb-2">
          고객님의 정보를 입력해주세요
        </h3>
        <p className='text-[#878A92] mb-6'>입력하신 번호로 안내 문자를 발송해드립니다.</p>
        
        <p className="text-[#46474C] text-[13px]">이름</p>
        <CommonInput
          inputValue={name}
          setInputValue={setName}
          placeholder="이름을 입력해주세요"
          disabled={isVerified}
        />

        <div className="mt-4">
          <p className="text-[#46474C] text-[13px]">휴대전화번호</p>
          <div className="relative flex gap-2">
            <div className="flex-1">
              <CommonInputPhoneNumber
                inputValue={phoneNumber}
                setInputValue={handlePhoneNumberChange}
                placeholder="휴대전화번호를 입력해주세요"
                disabled={isRequested}
                isValid={!isPhoneInputTouched || isValidPhoneFormat}
              />
              {phoneError && isPhoneInputTouched && (
                <p className="text-red-500 text-right text-[13px] mt-1">
                  잘못된 휴대전화번호입니다. 다시 입력해주세요.
                </p>
              )}
            </div>
            <button
              className={`px-4 absolute right-2 top-6 text-[15px] font-semibold ${
                isValidPhoneFormat
                  ? 'text-[#313A4C]'
                  : 'text-[#AEB0B6]'
              }`}
              onClick={handleRequestVerification}
              disabled={isRequested}
            >
              인증요청
            </button>
          </div>
        </div>

        {isRequested && !isVerified && (
          <div className="mt-4">
            <p className="text-[#46474C] text-[13px]">인증번호</p>
            <div className="relative flex gap-2">
              <div className="flex-1">
                <CommonInputPhoneNumber
                  inputValue={verificationCode}
                  setInputValue={setVerificationCode}
                  placeholder="인증번호를 입력해주세요"
                  isValid={!isError}
                />
              </div>
              <p className="text-[#AEB0B6] text-[15px] absolute right-24 top-6">
                {formatTime(timeLeft)}
              </p>
              <button
                className={`px-4 absolute right-2 top-6 text-[15px] font-semibold text-[#313A4C]`}
                onClick={handleVerifyCode}
              >
                인증확인
              </button>
            </div>
            <div className="flex justify-end mt-1">
              {isError && (
                <p className="text-red-500 text-[13px] text-right">
                  잘못된 인증번호입니다. 다시 입력해주세요.
                </p>
              )}
            </div>
          </div>
        )}

        {isVerified && (
          <>
            <div className="flex items-center justify-between p-3 mt-4 h-[54px] border border-[#D0D5DD] rounded-lg">
              <span className="text-[#1b1c1e]">{verificationCode}</span>
              <span className="text-[#46474C]"><img src="/assets/img/input-check.svg" alt="" /></span>
            </div>
            
            <div className="flex justify-end mt-1">
              <p className="text-[13px] text-right text-[#46474C]">
                인증되었습니다.
              </p>
            </div>
          </>
        )}

        <div className="flex justify-between mt-8 gap-[10px]">
          <button
            className="flex items-center justify-center w-full h-[52px] rounded-lg bg-[#F4F4F5] text-[#313A4C] font-semibold"
            onClick={() => setisShowPhoneNumber(false)}
          >
            취소
          </button>
          <button
            className={`flex items-center justify-center w-full h-[52px] rounded-lg ${
              isVerified ? 'bg-[#313A4C]' : 'bg-[#E1E2E4]'
            } text-white font-semibold`}
            onClick={handleComplete}
            disabled={!isVerified}
          >
            완료
          </button>
        </div>
      </div>
    </div>
  );
}

export default PopupBtmModifyPhoneNumber;